import { Component, type ErrorInfo, type ReactNode } from 'react';

interface BoundaryState {
  error: Error | null;
  info: string | null;
}

/**
 * Render-error boundary.
 *
 * A crash in one panel must not blank the console: the boundary localises the
 * failure, keeps the navigation usable and tells the operator which component
 * broke (React stack trimmed to the first frames, which is what a ticket needs).
 *
 * `onRetry` is how a retry becomes more than a re-render: `React.lazy` **caches** the promise it
 * was handed, so a chunk that failed to load is re-thrown by every later render and clearing this
 * boundary's state alone cannot recover. The shell passes a callback that asks the route for a
 * *fresh* lazy object (a fresh `import()`, a fresh chance) — see `app/lazyPage.tsx`.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string; onRetry?: () => void },
  BoundaryState
> {
  override state: BoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error, info: null };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console errors stay visible for the operator's devtools.
    console.error(
      `[acs-console] render error${this.props.label ? ` in ${this.props.label}` : ''}`,
      error,
      info,
    );
    this.setState({ info: info.componentStack ?? null });
  }

  private reset = (): void => {
    // The retry asks the route for a new chance *before* the children come back: the lazy object
    // that threw must not be the one that is mounted again.
    this.props.onRetry?.();
    this.setState({ error: null, info: null });
  };

  override render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const firstFrames = (info ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 3)
      .join(' ← ');

    return (
      <div
        className="panel border-danger/40 bg-danger-bg p-panel"
        role="alert"
        data-testid="error-boundary"
      >
        <h2 className="text-ui font-semibold text-danger-ink">
          نمایش این بخش متوقف شد{this.props.label ? ` — ${this.props.label}` : ''}
        </h2>
        <p className="mt-2 text-body text-ink-dim">
          باگ رندر باعث شد این پنل بسته شود؛ بقیهٔ کنسول همچنان کار می‌کند. اگر تکرار شد، این پیام
          را در تیکت بچسبانید.
        </p>
        <pre className="code-block mt-3" dir="ltr">
          {`${error.name}: ${error.message}${firstFrames ? `\n${firstFrames}` : ''}`}
        </pre>
        <div className="mt-3 flex gap-2">
          <button type="button" className="btn btn-ghost btn-sm" onClick={this.reset}>
            تلاش مجدد برای نمایش
          </button>
        </div>
      </div>
    );
  }
}
